import React from 'react'
import {
  Icon,
  Stack,
  Text,
  IconButton,
  DirectionalHint,
  IColumn,
  TooltipHost,
  ProgressIndicator,
  ContextualMenuItemType,
  DefaultButton,
  Spinner,
  SpinnerSize,
} from 'office-ui-fabric-react'
import { ChildProps, graphql } from 'react-apollo'
import moment from 'moment'
import {
  setCampaignStatus,
  setCampaignRestrictedStatus,
  CampaignSummaryResponse,
  CampaignSummary,
  Campaign,
  getCampaignsQuery_v2,
  CampaignCollectionSummary,
  getNextTitle,
  AssetTagsResponse,
  CampaignStatus,
} from '../../Api/campaigns'
import { TableView } from '../../Components/TableView'
import './CampaignList.css'
import { CancelCampaignDialog } from './CancelCampaignDialog'
import { DeleteCampaignDialog } from './DeleteCampaignDialog'
import { EditCampaignDialog } from './CampaignDialog/EditCampaignDialog'
import { ViewCampaignDialog } from './CampaignDialog/ViewCampaignDialog'
import { CreateCampaignDialog } from './CampaignDialog/CreateCampaignDialog'
import { removeTimezoneOffset } from '../../Utils/date'
import { isTemplateTitle } from '../../Functions/templateTitle'
import MigrationCampaignDialog from './CampaignDialog/MigrationCampaignDialog'
import { getAssetTags } from '../../Functions/assetTags'
import { ToastContainer, toast } from 'react-toastify'
import { SessionContext } from '../../Contexts/SessionContext'
import { MultiEditCampaignDialog } from './CampaignDialog/MultiEdit/MultiEditCampaignDialog'
import {
  addEntityIdToHash,
  clearEntityIdFromHash,
  getEntityIdFromHash,
} from '../../Utils/hash'

type StatusFilter = 'all' | 'active' | 'paused' | 'completed' | 'cancelled'

const STATUS_FILTERS: Record<
  StatusFilter,
  { label: string; statuses: CampaignStatus[] }
> = {
  all: { label: 'All', statuses: [] },
  active: { label: 'Active', statuses: ['live', 'not_started', 'transcoding'] },
  paused: { label: 'Paused', statuses: ['paused'] },
  completed: { label: 'Completed', statuses: ['expired', 'limited'] },
  cancelled: {
    label: 'Cancelled',
    statuses: ['cancelled', 'transcoding_failed'],
  },
}

interface State {
  openModal?: {
    modalType: 'cancel' | 'delete' | 'template'
    campaign: CampaignRowItem
  }
  openMigrateModal?: boolean
  openEditModal?: boolean
  statusFilters: StatusFilter[]
  textFilteredAssets?: CampaignRowItem[]
  statusChanges: {
    id: string
    fromStatus: CampaignStatus
    toStatus: CampaignStatus
    loading: boolean
  }[]
  selectedCampaigns: Campaign[]
  expandedGroupIds: Set<string>
}

interface InputProps {
  collectionId: string
  campaignCollections?: CampaignCollectionSummary[]
  isAdminOrAccountManager: boolean
  selectCampaignMode: boolean
  onCancel: () => void
}

type OmitPriority<T> = Pick<T, Exclude<keyof T, 'priority'>>

export interface CampaignRowItem extends OmitPriority<CampaignSummary> {
  status: CampaignStatus
  displayStatus: CampaignStatus
  collectionId: string
  priority: number | number[] // array on a group row: deduped priorities of its children
  cappingEventType: string | null
  totalDays?: number
  startDate: string
  endDate: string | null
  daysSinceStarted: number
  displayedTargetedAssets: string[]
  // displayedTargetedAssets plus every placement name covered by a
  // tag-based (not per-placement) targeting entry, so filtering by
  // placement name also finds campaigns targeted via that placement's tag
  tagsSearchable: string[]
  groupedCampaigns?: CampaignRowItem[] // set on a synthetic row grouping several placeholder campaigns
  isGroupChild?: boolean // set on a real row shown under an expanded group
  groupLabel?: string // display text for a group row; title stays the plain source name
}

const PLACEHOLDER_AD_SOURCE_LABELS: { [key: string]: string } = {
  video: 'Video',
  banner: 'Banner',
  otherOutstreamPlayers: 'Passback',
}

// PL-6435: collapse per-placement-per-ad-source campaigns into one row per
// ad source, regardless of which placeholder rule spawned them - one group
// per ad source per collection, not one per (rule, ad source) pair.
function groupPlaceholderRows(rows: CampaignRowItem[]): CampaignRowItem[] {
  const groups = new Map<string, CampaignRowItem[]>()
  const ungrouped: CampaignRowItem[] = []
  for (const row of rows) {
    const placeholder = row.asset && row.asset.placeholder
    const adSource = placeholder && placeholder.allowedAdSources[0]
    if (!adSource) {
      ungrouped.push(row)
      continue
    }
    const key = `${row.collectionId}::${adSource}`
    const group = groups.get(key)
    if (group) {
      group.push(row)
    } else {
      groups.set(key, [row])
    }
  }
  groups.forEach((group, key) => {
    if (group.length === 1) {
      ungrouped.push(group[0])
      return
    }
    const adSource = group[0].asset.placeholder!.allowedAdSources[0]
    const adSourceLabel = PLACEHOLDER_AD_SOURCE_LABELS[adSource] || adSource
    ungrouped.push({
      ...group[0],
      id: `group:${key}`,
      title: group.map((c) => c.title).join(' | '), // drives Name filter/sort; groupLabel renders
      groupLabel: `R2B2 monetization (${adSourceLabel})`,
      tagsSearchable: Array.from(
        new Set(group.flatMap((c) => c.tagsSearchable))
      ),
      priority: Array.from(new Set(group.map((c) => c.priority as number))),
      groupedCampaigns: group,
    })
  })
  return ungrouped
}

// inserts each expanded group's children right after it as indented rows;
// ponytail: only stays adjacent under default order, sorting scatters them
function expandGroupedRows(
  rows: CampaignRowItem[],
  expandedGroupIds: Set<string>
): CampaignRowItem[] {
  return rows.flatMap((row) => {
    if (!row.groupedCampaigns || !expandedGroupIds.has(row.id)) {
      return [row]
    }
    return [
      row,
      ...row.groupedCampaigns.map((c) => ({ ...c, isGroupChild: true })),
    ]
  })
}

class CampaignListView extends React.Component<
  ChildProps<InputProps, CampaignSummaryResponse>,
  State
> {
  static contextType = SessionContext
  context!: React.ContextType<typeof SessionContext>

  constructor(params: any) {
    super(params)
    this.state = {
      openModal: undefined,
      statusFilters: [],
      textFilteredAssets: undefined,
      statusChanges: [],
      selectedCampaigns: [],
      expandedGroupIds: new Set(),
    }
  }
  render() {
    const { error } = this.props.data!
    if (error) {
      window.console.error(error)
      return this.renderError()
    }
    const isLoading = this.props.data!.loading
    const assets = isLoading ? undefined : this.getAssets()
    const columns = this.getColumns(this.props.data!.refetch)
    if (!isLoading && assets && assets.length === 0) {
      return this.renderEmpty()
    }

    const selectedFilters = this.state.statusFilters
    const combinedStatuses = selectedFilters.flatMap(
      (f) => STATUS_FILTERS[f].statuses
    )
    const statusFilter =
      selectedFilters.length === 0
        ? undefined
        : (a: CampaignRowItem) =>
            a.groupedCampaigns
              ? a.groupedCampaigns.some((c) =>
                  combinedStatuses.includes(c.displayStatus)
                )
              : combinedStatuses.includes(a.displayStatus)

    return (
      <div style={{ width: '100%' }}>
        <ToastContainer />
        {!isLoading &&
          this.renderStatusFilter(
            assets,
            this.state.textFilteredAssets,
            this.state.statusFilters
          )}
        <TableView
          cacheKey={this.props.collectionId}
          columns={columns}
          filterableColumnKeys={['name', 'tags', 'priority']}
          postSort={(a, b, _) => {
            if (a.status === 'cancelled' && b.status !== 'cancelled') return 1
            if (a.status !== 'cancelled' && b.status === 'cancelled') return -1
            return 0
          }}
          rowToCacheKey={(row) =>
            this.props.collectionId === 'all' ? 'all' : row.collectionId
          }
          rows={assets}
          onRowClick={(row: CampaignRowItem, event: { button: number }) => {
            if (row.groupedCampaigns) {
              this.toggleGroupExpanded(row)
            } else if (event.button === 1) {
              window.open(
                `${process.env.PUBLIC_URL}/#/${this.context.ctok}/campaigns/${row.collectionId}/#/edit/${row.id}`,
                '_blank'
              )
            } else {
              this.editCampaign(row)
            }
          }}
          isSelectable={this.props.selectCampaignMode}
          onSelectionChanged={(selection) =>
            this.setState({
              selectedCampaigns: selection.map((s) => s as Campaign),
            })
          }
          onCancel={this.props.onCancel}
          large={true}
          rowFilter={statusFilter}
          onFilteredRowsChange={(filteredRows) =>
            this.setState({
              textFilteredAssets: filteredRows as CampaignRowItem[] | undefined,
            })
          }
        />
        {this.renderCancelCampaignDialog(this.props.data!.refetch)}
        {this.renderDeleteCampaignDialog(this.props.data!.refetch)}
        {this.renderEditCampaignDialog(this.props.data!.refetch)}
        {this.renderTemplateCampaignDialog(this.props.data!.refetch)}
        {this.renderEditDialog(this.props.data!.refetch)}
        {this.renderMigrationDialog(this.props.data!.refetch)}
        {this.props.selectCampaignMode && this.renderEditButton()}
        {this.props.selectCampaignMode && this.renderMigrateButton()}
      </div>
    )
  }
  renderStatusFilter(
    assets: CampaignRowItem[] | undefined,
    textFilteredAssets: CampaignRowItem[] | undefined,
    statusFilters: StatusFilter[]
  ) {
    const visible = textFilteredAssets || assets || []
    const total = visible.length
    const hasTextFilter = textFilteredAssets !== undefined
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 8 }}>
        {(Object.keys(STATUS_FILTERS) as StatusFilter[]).map((key) => {
          const { label, statuses } = STATUS_FILTERS[key]
          const isSelected =
            key === 'all'
              ? statusFilters.length === 0
              : statusFilters.includes(key)
          const allForCategory = statuses.length
            ? (assets || []).filter((a) => statuses.includes(a.displayStatus))
                .length
            : (assets || []).length
          const visibleForCategory = statuses.length
            ? visible.filter((a) => statuses.includes(a.displayStatus)).length
            : total
          const headerText = hasTextFilter
            ? `${label} (${visibleForCategory}/${allForCategory})`
            : `${label} (${allForCategory})`
          return (
            <button
              key={key}
              onClick={() => this.toggleStatusFilter(key)}
              style={{
                background: 'none',
                border: 'none',
                borderBottom: isSelected
                  ? '2px solid #0078d4'
                  : '2px solid transparent',
                color: isSelected ? '#0078d4' : '#323130',
                padding: '0 8px',
                height: 44,
                cursor: 'pointer',
              }}
            >
              <span
                style={{
                  display: 'block',
                  fontWeight: 600,
                  visibility: 'hidden',
                  height: 0,
                }}
              >
                {headerText}
              </span>
              <span style={{ fontWeight: isSelected ? 600 : 400 }}>
                {headerText}
              </span>
            </button>
          )
        })}
      </div>
    )
  }
  toggleStatusFilter(key: StatusFilter) {
    const { statusFilters } = this.state
    if (key === 'all') {
      this.setState({ statusFilters: [] })
      return
    }
    const newFilters = statusFilters.includes(key)
      ? statusFilters.filter((f) => f !== key)
      : [...statusFilters, key]
    const allSpecificKeys = (
      Object.keys(STATUS_FILTERS) as StatusFilter[]
    ).filter((f) => f !== 'all')
    const allSelected = allSpecificKeys.every((f) => newFilters.includes(f))
    this.setState({
      statusFilters: newFilters.length === 0 || allSelected ? [] : newFilters,
    })
  }
  renderError() {
    return (
      <Stack
        horizontalAlign="center"
        verticalAlign="center"
        verticalFill
        style={{ width: '100%' }}
      >
        <Text
          variant="xLarge"
          style={{
            maxWidth: 600,
            padding: 20,
            textAlign: 'center',
          }}
        >
          This placement collection could not be found. Most likely, you've
          opened an older link of a collection that is now deleted. If the error
          persists and your collection can not be selected from the navigation
          menu, please contact support.
        </Text>
      </Stack>
    )
  }
  renderEmpty() {
    return (
      <Stack
        horizontalAlign="center"
        verticalAlign="center"
        verticalFill
        style={{ width: '100%' }}
      >
        <Text
          variant="xLarge"
          style={{
            maxWidth: 600,
            padding: 20,
            textAlign: 'center',
          }}
        >
          This campaign collection is empty. You can add a campaign by pressing
          the <i>Create campaign</i>{' '}
          <Icon iconName="Add" style={{ verticalAlign: 'middle' }} />
          &nbsp;button.
        </Text>
      </Stack>
    )
  }
  renderCancelCampaignDialog(refetch: () => void) {
    if (!this.state.openModal || this.state.openModal!.modalType !== 'cancel') {
      return <React.Fragment />
    }
    return (
      <CancelCampaignDialog
        collectionId={this.state.openModal!.campaign.collectionId}
        campaignInfo={
          this.state.openModal!.campaign as unknown as CampaignSummary
        }
        onUpdated={() => refetch()}
        onClose={() => this.setState({ openModal: undefined })}
      />
    )
  }
  renderDeleteCampaignDialog(refetch: () => void) {
    if (!this.state.openModal || this.state.openModal!.modalType !== 'delete') {
      return <React.Fragment />
    }
    return (
      <DeleteCampaignDialog
        collectionId={this.state.openModal!.campaign.collectionId}
        campaignInfo={
          this.state.openModal!.campaign as unknown as CampaignSummary
        }
        onUpdated={() => refetch()}
        onClose={() => this.setState({ openModal: undefined })}
      />
    )
  }
  renderTemplateCampaignDialog(refetch: () => void) {
    if (
      !this.state.openModal ||
      this.state.openModal!.modalType !== 'template'
    ) {
      return <React.Fragment />
    }

    const getBeginningOfDayTimestamp = () => {
      const date = new Date()
      date.setUTCHours(0, 0, 0, 0)
      return date.getTime()
    }
    const getTimestamp = (date: string) => new Date(date).getTime()

    const oldCampaign = this.props
      .data!.campaignCollections!.flatMap((cc) => cc.assets)
      .find((c) => c.id === this.state.openModal!.campaign.id) as
      | Campaign
      | undefined
    let newCampaign: Campaign | undefined = undefined
    if (oldCampaign) {
      newCampaign = { ...oldCampaign }
      if (
        !isTemplateTitle(
          oldCampaign.title,
          oldCampaign,
          this.props.data! as AssetTagsResponse
        )
      ) {
        newCampaign.title = getNextTitle(oldCampaign.title)
      }
      newCampaign.creationDate = new Date().toISOString()
      if (getTimestamp(oldCampaign.startDate) >= getBeginningOfDayTimestamp()) {
        newCampaign.startDate = oldCampaign.startDate
        newCampaign.endDate = oldCampaign.endDate
      } else {
        newCampaign.startDate = newCampaign.creationDate
        newCampaign.endDate = null
      }
      newCampaign.collectionId = this.props.data!.campaignCollections!.find(
        (cc) => !!cc.assets.find((a) => a.id === newCampaign!.id)
      )!.id
    }
    return (
      <CreateCampaignDialog
        collectionId={this.state.openModal!.campaign.collectionId}
        isTemplateMode={true}
        campaignCollections={this.props.campaignCollections}
        initialCampaign={newCampaign}
        isAdminOrAccountManager={this.props.isAdminOrAccountManager}
        onUpdated={() => refetch()}
        onClose={() => this.setState({ openModal: undefined })}
      />
    )
  }
  renderEditCampaignDialog(refetch: () => void) {
    const id = getEntityIdFromHash()
    if (!id) {
      return <React.Fragment />
    }
    const campaignCollections = this.props.data!.campaignCollections
    if (!campaignCollections || !campaignCollections[0]) {
      return <React.Fragment />
    }
    const c = campaignCollections
      .flatMap((cc) => cc.assets)
      .find((c) => c.id === id) || {
      status: 'cancelled',
    }
    const isPaused = c.status === 'paused'
    const isLive = c.status === 'live' || c.status === 'transcoding'
    const notStarted = c.status === 'not_started'
    const isActive = isLive || isPaused || notStarted
    if (!isActive) {
      return (
        <ViewCampaignDialog
          collectionId={this.props.collectionId}
          id={id}
          onUpdated={() => this.templateCampaign(c as CampaignRowItem)}
          isAdminOrAccountManager={this.props.isAdminOrAccountManager}
          onClose={() => clearEntityIdFromHash()}
        />
      )
    }
    return (
      <EditCampaignDialog
        collectionId={this.props.collectionId}
        id={id}
        isAdminOrAccountManager={this.props.isAdminOrAccountManager}
        onUpdated={() => refetch()}
        onClose={() => clearEntityIdFromHash()}
      />
    )
  }
  renderEditButton() {
    return (
      <Stack
        horizontal
        horizontalAlign="start"
        styles={{
          root: {
            padding: 5,
            position: 'absolute',
            transition: 'all 0.3s',
            width: 'auto',
            top: 160,
            right: 20,
          },
        }}
      >
        <DefaultButton
          onClick={() => {
            this.setState({ openEditModal: true })
          }}
          text={'Edit'}
          disabled={this.state.selectedCampaigns.length === 0}
          iconProps={{ iconName: 'Edit' }}
          styles={{ root: { width: 120 } }}
        />
      </Stack>
    )
  }
  renderMigrateButton() {
    return (
      <Stack
        horizontal
        horizontalAlign="start"
        styles={{
          root: {
            padding: 5,
            position: 'absolute',
            transition: 'all 0.3s',
            width: 'auto',
            top: 210,
            right: 20,
          },
        }}
      >
        <DefaultButton
          onClick={() => {
            this.setState({ openMigrateModal: true })
          }}
          text={'Migrate'}
          disabled={this.state.selectedCampaigns.length === 0}
          iconProps={{ iconName: 'Rocket' }}
          styles={{ root: { width: 120 } }}
        />
      </Stack>
    )
  }
  renderEditDialog(refetch: () => Promise<any>) {
    if (this.state.openEditModal !== true) {
      return <React.Fragment />
    }
    return (
      <MultiEditCampaignDialog
        campaignsToEdit={this.state.selectedCampaigns}
        onUpdated={async () => {
          this.setState({ openEditModal: false })
          await refetch()
          const assets = this.getAssets() || []
          this.setState({
            selectedCampaigns: assets.filter((campaign) =>
              this.state.selectedCampaigns.some(({ id }) => campaign.id === id)
            ) as unknown as Campaign[],
          })
        }}
        onClose={() => {
          this.setState({ openEditModal: false })
        }}
      />
    )
  }
  renderMigrationDialog(refetch: () => void) {
    if (this.state.openMigrateModal !== true) {
      return <React.Fragment />
    }
    const originalTags = getAssetTags(this.props.data! as AssetTagsResponse)
    const hasCorrectId = (collection: CampaignCollectionSummary) =>
      collection!.id === this.props.collectionId
    return (
      <MigrationCampaignDialog
        assetTagInfo={this.props.data! as AssetTagsResponse}
        campaignsToMigrate={this.state.selectedCampaigns}
        originalCollectionTitle={
          this.props.collectionId === 'all'
            ? 'all'
            : this.props.data!.campaignCollections!.find(hasCorrectId)!.title
        }
        originalTags={originalTags}
        onUpdated={() => refetch()}
        onClose={() => {
          this.setState({ selectedCampaigns: [] })
          this.props.onCancel()
          this.setState({ openMigrateModal: false })
        }}
        onSuccess={({ campaignTitle, user }) =>
          toast.success(`campaign ${campaignTitle} was created for ${user}`)
        }
      />
    )
  }
  // A targetedAssets entry of "collectionId/tag" can target a single placement
  // (tag === placement id) or a whole group of placements sharing a custom tag.
  // For the group case, resolve the member placement titles so filtering by
  // placement name also surfaces campaigns targeted via the group's tag.
  getPlacementNamesForTag(collectionId: string, tag: string): string[] {
    const {
      outstreamPlacementCollections_v2 = [],
      instreamPlacementCollections_v2 = [],
    } = this.props.data!
    const collection =
      outstreamPlacementCollections_v2!.find((c) => c.id === collectionId) ||
      instreamPlacementCollections_v2!.find((c) => c.id === collectionId)
    if (!collection) {
      return []
    }
    if (tag === '*') {
      return collection.assets.map((a) => a.title)
    }
    if (collection.assets.some((a) => a.id === tag)) {
      return []
    }
    return collection.assets
      .filter((a) => a.tags.includes(tag))
      .map((a) => a.title)
  }
  getAssets(): CampaignRowItem[] | undefined {
    const campaignCollections = this.props.data!.campaignCollections!
    if (!campaignCollections || campaignCollections.length === 0) {
      return
    }
    const mapCampaign = (
      collection: CampaignCollectionSummary,
      c: CampaignSummary
    ): CampaignRowItem => {
      const dayInMs = 24 * 60 * 60e3
      const totalDays = c.endDate
        ? Math.floor(
            (new Date(c.endDate).getTime() - new Date(c.startDate).getTime()) /
              dayInMs
          )
        : undefined
      const lastActiveDay =
        c.status === 'limited'
          ? c.lastEventTs!
          : Math.min(Date.now(), new Date(c.endDate || Date.now()).getTime())
      const daysSinceStarted = Math.min(
        Math.max(
          Math.floor(
            (new Date(lastActiveDay).getTime() -
              new Date(c.startDate).getTime()) /
              dayInMs
          ),
          0
        ),
        totalDays || 1e9
      )
      const rawStatus = c.status as CampaignStatus
      const change = this.state.statusChanges.find((sc) => sc.id === c.id)
      // change is stale once the server reports a status we didn't toggle from
      const displayStatus =
        change && rawStatus === change.fromStatus ? change.toStatus : rawStatus
      const targetedPairs = c.targetedAssets.map((t) => t.split('/'))
      const displayedTargetedAssets = targetedPairs
        .map(([collectionId, tagName]) => {
          const {
            outstreamPlacementCollections_v2 = [],
            instreamPlacementCollections_v2 = [],
          } = this.props.data!
          const outstreamPlacementCollection_v2 =
            outstreamPlacementCollections_v2!.find((c) => c.id === collectionId)
          if (outstreamPlacementCollection_v2) {
            const asset = outstreamPlacementCollection_v2.assets.find(
              ({ id }) => id === tagName
            )
            const displayTagName = asset ? asset.title : tagName
            return [outstreamPlacementCollection_v2.title, displayTagName]
          }
          const instreamPlacementCollection_v2 =
            instreamPlacementCollections_v2!.find((c) => c.id === collectionId)
          if (instreamPlacementCollection_v2) {
            const asset = instreamPlacementCollection_v2.assets.find(
              ({ id }) => id === tagName
            )
            const displayTagName = asset ? asset.title : tagName
            return [instreamPlacementCollection_v2.title, displayTagName]
          }
          const partnershipTag = this.props
            .data!.publisherPartnerships_v2!.reduce(
              (ret, p) => [...ret, ...p.sharedAssetTags],
              [] as {
                collectionId: string
                collectionTitle: string
                tagName: string
                displayName: string
              }[]
            )
            .find(
              (p) => p.collectionId === collectionId && p.tagName === tagName
            )
          if (partnershipTag) {
            return [partnershipTag.collectionTitle, partnershipTag.displayName]
          }
          if (collectionId === 'integrations') {
            return [tagName]
          }
          return null
        })
        .filter(Boolean)
        .map((t) => t!.join('/'))
      return {
        ...c,
        status: rawStatus,
        displayStatus,
        endDate: c.endDate ? c.endDate : '',
        cappingEventType: c.cappingEventType || null,
        eventCount: c.eventCount,
        lastCappingEventType: c.lastCappingEventType,
        collectionId: collection.id,
        totalDays,
        daysSinceStarted,
        displayedTargetedAssets,
        tagsSearchable: [
          ...displayedTargetedAssets,
          ...targetedPairs.flatMap(([collectionId, tagName]) =>
            this.getPlacementNamesForTag(collectionId, tagName)
          ),
        ],
      }
    }

    const grouped = groupPlaceholderRows(
      campaignCollections.reduce((acc: CampaignRowItem[], collection) => {
        return acc.concat(
          collection.assets.map((c) => mapCampaign(collection, c))
        )
      }, [])
    )
    return expandGroupedRows(grouped, this.state.expandedGroupIds)
  }

  getColumns(refetch: () => Promise<any>): IColumn[] {
    return [
      {
        key: 'status',
        name: '',
        fieldName: 'status',
        minWidth: 20,
        maxWidth: 20,
        isRowHeader: true,
        isIconOnly: true,
        isPadded: false,
        onRender: (a: CampaignRowItem) => this.renderStatusIcon(a),
      },
      {
        key: 'name',
        name: 'Title',
        fieldName: 'title',
        isRowHeader: true,
        isPadded: true,
        minWidth: 150,
        maxWidth: 350,
        onRender: (a: CampaignRowItem) =>
          a.groupedCampaigns ? (
            // expand toggle lives in the status column, no second chevron here
            <span>{a.groupLabel}</span>
          ) : (
            <a
              className="title-link-class"
              style={a.isGroupChild ? { paddingLeft: 22 } : undefined}
              href={`${window.location.hash}/#/edit/${a.id}`.replace(
                /\/+/g,
                '/'
              )}
            >
              <span>{a.title}</span>
            </a>
          ),
      },
      {
        key: 'progress',
        name: 'Progress',
        fieldName: 'eventCount',
        minWidth: 100,
        maxWidth: 100,
        isPadded: false,
        onRender: (a: CampaignRowItem) => this.renderEventProgressCell(a),
      },
      {
        key: 'startDate',
        name: 'Start Date',
        fieldName: 'startDate',
        minWidth: 100,
        maxWidth: 100,
        isPadded: false,
        onRender: (a: CampaignRowItem) => (
          <span>{moment(a.startDate).format('DD/MM/YYYY')}</span>
        ),
      },
      {
        key: 'endDate',
        name: 'End Date',
        fieldName: 'endDate',
        minWidth: 100,
        maxWidth: 100,
        isPadded: true,
        onRender: (a: CampaignRowItem) => this.renderTimeProgressCell(a),
      },
      {
        key: 'tags',
        name: 'Tags',
        // ponytail: fieldName drives both sort and the header filter; using
        // tagsSearchable (adds tag-group placement names) means sorting a
        // tag-group row can shift slightly vs. sorting on the display text
        // alone. Split fieldName into separate sort/filter fields if that bites.
        fieldName: 'tagsSearchable',
        minWidth: 150,
        maxWidth: 500,
        isPadded: true,
        onRender: (a: CampaignRowItem) => (
          <span>
            {(a.groupedCampaigns
              ? Array.from(
                  new Set(
                    a.groupedCampaigns.flatMap((c) => c.displayedTargetedAssets)
                  )
                )
              : a.displayedTargetedAssets
            ).join(', ')}
          </span>
        ),
      },
      {
        key: 'priority',
        name: 'Priority',
        fieldName: 'priority',
        minWidth: 30,
        maxWidth: 100,
        isPadded: true,
        onRender: (a: CampaignRowItem) => (
          <span>
            {Array.isArray(a.priority) ? a.priority.join(', ') : a.priority}
          </span>
        ),
      },
      {
        key: 'menu',
        name: '',
        fieldName: '',
        minWidth: 20,
        maxWidth: 20,
        isIconOnly: true,
        onRender: (a: CampaignRowItem) => {
          // no per-campaign actions on a synthetic group row
          if (a.groupedCampaigns) {
            return null
          }
          return (
            <IconButton
              styles={{
                root: {
                  height: 18,
                },
                menuIcon: {
                  fontWeight: 'bold',
                },
              }}
              menuProps={{
                coverTarget: true,
                directionalHint: DirectionalHint.topCenter,
                items: [
                  {
                    key: 'resume',
                    text: 'Resume',
                    iconProps: { iconName: 'streaming' },
                    onClick: () => this.toggleStatus(a),
                  },
                  {
                    key: 'pause',
                    text: 'Pause',
                    iconProps: { iconName: 'pause' },
                    onClick: () => this.toggleStatus(a),
                  },
                  {
                    key: 'divider_1',
                    itemType: ContextualMenuItemType.Divider,
                  },
                  {
                    key: 'edit',
                    text: 'Edit',
                    iconProps: { iconName: 'edit' },
                    onClick: () => this.editCampaign(a),
                  },
                  {
                    key: 'view',
                    text: 'View',
                    iconProps: { iconName: 'view' },
                    onClick: () => this.editCampaign(a),
                  },
                  {
                    key: 'template',
                    text: 'Use as template',
                    iconProps: { iconName: 'copy' },
                    onClick: () => this.templateCampaign(a),
                  },
                  {
                    key: 'divider_2',
                    itemType: ContextualMenuItemType.Divider,
                  },
                  {
                    key: 'cancel',
                    text: 'Cancel',
                    iconProps: { iconName: 'cancel' },
                    onClick: () => this.cancelCampaignPopup(a),
                  },
                  {
                    key: 'delete',
                    text: 'Delete',
                    iconProps: { iconName: 'delete' },
                    onClick: () => this.deleteCampaign(a),
                  },
                  {
                    key: 'restrict',
                    text: 'Restrict',
                    iconProps: { iconName: 'lock' },
                    onClick: () => this.toggleRestrictedStatus(a, refetch),
                  },
                  {
                    key: 'unrestrict',
                    text: 'Unrestrict',
                    iconProps: { iconName: 'unlock' },
                    onClick: () => this.toggleRestrictedStatus(a, refetch),
                  },
                ].filter((item) => {
                  const isPaused = a.displayStatus === 'paused'
                  const isLive = a.displayStatus === 'live'
                  const isTranscoding = a.displayStatus === 'transcoding'
                  const notStarted = a.displayStatus === 'not_started'
                  const isActive =
                    isLive || isPaused || notStarted || isTranscoding
                  if (item.key === 'resume') {
                    return isPaused
                  }
                  if (item.key === 'pause') {
                    return isLive
                  }
                  if (item.key === 'cancel') {
                    return (
                      isActive &&
                      !(a.restricted && !this.props.isAdminOrAccountManager)
                    )
                  }
                  if (item.key === 'edit') {
                    return isActive
                  }
                  if (item.key === 'view') {
                    return !isActive
                  }
                  if (item.key === 'delete') {
                    return !isActive
                  }
                  if (item.key === 'restrict') {
                    return this.props.isAdminOrAccountManager && !a.restricted
                  }
                  if (item.key === 'unrestrict') {
                    return this.props.isAdminOrAccountManager && a.restricted
                  }
                  if (item.key === 'template') {
                    return this.isValidTemplate(a)
                  }
                  return true
                }),
              }}
            />
          )
        },
      },
    ]
  }
  renderEventProgressCell(c: CampaignRowItem) {
    if (c.groupedCampaigns) {
      return null // events are per-campaign, no single number for a group row
    }
    if (c.restricted) {
      return <span>{'Restricted'}</span>
    }
    const eventCountAndLimitString = c.eventLimit
      ? `${this.shortNumber(c.eventCount)} / ${this.shortNumber(c.eventLimit)}`
      : `${this.shortNumber(c.eventCount)}`
    const eventString = ` ${eventTypeToDisplay(c.cappingEventType || 'impression', c.eventLimit || c.eventCount)}`
    const [r, g, b] = [255, 171, 0]
    const styles = this.getProgressStyles(r, g, b)
    const isAvailable = c.lastCappingEventType === c.cappingEventType
    const progressHidden = !c.eventLimit || !isAvailable
    if (!progressHidden) {
      styles.itemName.marginTop = -2
    }
    const label = isAvailable ? (
      eventCountAndLimitString + eventString
    ) : (
      <i>fetching updates...</i>
    )
    const percentComplete = c.eventCount / (c.eventLimit || 1e9)
    return (
      <ProgressIndicator
        label={label}
        percentComplete={percentComplete}
        progressHidden={progressHidden}
        styles={styles}
      />
    )
  }
  renderTimeProgressCell(c: CampaignRowItem) {
    const endDate = c.endDate
      ? moment(removeTimezoneOffset(c.endDate)).format('DD/MM/YYYY')
      : ''
    const [r, g, b] = [1, 87, 155]
    const styles = this.getProgressStyles(r, g, b)
    const progressHidden = !c.totalDays
    if (!progressHidden) {
      styles.itemName.marginTop = -2
    }
    return (
      <ProgressIndicator
        label={endDate}
        percentComplete={c.daysSinceStarted / (c.totalDays || 0)}
        progressHidden={progressHidden}
        styles={styles}
      />
    )
  }
  shortNumber(n: number) {
    return (
      (n < 1e3 && `${n}`) ||
      (n < 1e6 && `${(n / 1e3).toFixed(2)}K`) ||
      (n < 1e9 && `${(n / 1e6).toFixed(2)}M`) ||
      `${(n / 1e9).toFixed(2)}B`
    ).replace(/\.0{1,2}(K|M|B)$/, '$1')
  }
  getProgressStyles(r: number, g: number, b: number) {
    return {
      itemName: {
        fontSize: 12,
        paddingTop: 2,
        lineHeight: 'unset',
        marginTop: 0,
      },
      itemProgress: {
        paddingBottom: 0,
        paddingTop: 4,
      },
      progressBar: { background: `rgba(${r}, ${g}, ${b}, 1)` },
      progressTrack: { background: `rgba(${r}, ${g}, ${b}, 0.3)` },
    }
  }
  renderStatusIcon(c: CampaignRowItem) {
    if (c.groupedCampaigns) {
      // expand toggle instead of pause - a group has no single status
      return (
        <IconButton
          iconProps={{
            iconName: this.state.expandedGroupIds.has(c.id)
              ? 'ChevronDown'
              : 'ChevronRight',
          }}
          onClick={() => this.toggleGroupExpanded(c)}
        />
      )
    }
    const isLoading = this.state.statusChanges.some(
      (s) => s.id === c.id && s.loading
    )
    if (isLoading) {
      return (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spinner size={SpinnerSize.small} />
        </div>
      )
    }
    const status = c.displayStatus
    const isButton = status === 'live' || status === 'paused'
    const statusText =
      (status === 'live' && 'Live. Click to pause.') ||
      (status === 'paused' && 'Paused. Click to resume.') ||
      (status === 'not_started' &&
        `Starting on ${moment(c.startDate).format('DD/MM/YYYY')}`) ||
      (status === 'expired' &&
        `Ended on ${moment(c.startDate).format('DD/MM/YYYY')}`) ||
      (status === 'limited' &&
        `Finished with ${this.shortNumber(c.eventCount)} events`) ||
      (status === 'transcoding' && `Waiting for asset transcoding`) ||
      (status === 'transcoding_failed' &&
        `Cancelled due to transcoding failure`) ||
      (status === 'cancelled' && `Cancelled`) ||
      `Unknown status "${c.status}"`
    return (
      <TooltipHost
        styles={{
          root: {
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
          },
        }}
        className={isButton ? '' : 'table-tooltip'}
        content={statusText}
        tooltipProps={{
          delay: 0,
          directionalHint: DirectionalHint.leftCenter,
          hidden: this.props.selectCampaignMode,
        }}
      >
        <IconButton
          iconProps={{
            iconName:
              (status === 'live' && 'Streaming') ||
              (status === 'paused' && 'CirclePause') ||
              (status === 'limited' && 'CheckMark') ||
              (status === 'expired' && 'CheckMark') ||
              (status === 'not_started' && 'DateTime2') ||
              (status === 'transcoding' && 'DateTime2') ||
              'Blocked',
          }}
          disabled={!isButton}
          onClick={async () => this.toggleStatus(c)}
        />
      </TooltipHost>
    )
  }
  toggleGroupExpanded(row: CampaignRowItem) {
    const expandedGroupIds = new Set(this.state.expandedGroupIds)
    if (expandedGroupIds.has(row.id)) {
      expandedGroupIds.delete(row.id)
    } else {
      expandedGroupIds.add(row.id)
    }
    this.setState({ expandedGroupIds })
  }
  async toggleStatus(c: CampaignRowItem) {
    const currentStatus = c.displayStatus
    const isButton = currentStatus === 'live' || currentStatus === 'paused'
    if (!isButton) {
      return
    }
    const newStatus = currentStatus === 'live' ? 'paused' : 'live'
    const statusChangesWithoutC = this.state.statusChanges.filter(
      (s) => s.id !== c.id
    )
    this.setState({
      statusChanges: [
        ...statusChangesWithoutC,
        {
          id: c.id,
          fromStatus: c.status,
          toStatus: newStatus,
          loading: true,
        },
      ],
    })
    try {
      await setCampaignStatus(c.collectionId, c.id, newStatus)
      this.setState({
        statusChanges: [
          ...this.state.statusChanges.filter((s) => s.id !== c.id),
          {
            id: c.id,
            fromStatus: c.status,
            toStatus: newStatus,
            loading: false,
          },
        ],
      })
    } catch (e) {
      window.console.error(e)
      this.setState({
        statusChanges: this.state.statusChanges.filter((s) => s.id !== c.id),
      })
      toast.error(
        `Failed to ${
          newStatus === 'paused' ? 'pause' : 'resume'
        } campaign "${c.title}". Please try again.`
      )
    }
  }
  async toggleRestrictedStatus(
    c: CampaignRowItem,
    refetch: () => Promise<any>
  ) {
    await setCampaignRestrictedStatus(c.collectionId, c.id, !c.restricted)
    await refetch()
  }
  editCampaign(c: CampaignRowItem) {
    addEntityIdToHash(c.id)
  }
  templateCampaign(c: CampaignRowItem) {
    this.setState({
      openModal: {
        campaign: c,
        modalType: 'template',
      },
    })
  }
  isValidTemplate(c: CampaignRowItem) {
    return (
      (!c.restricted || this.props.isAdminOrAccountManager) &&
      !['transcoding_failed', 'transcoding'].includes(c.status)
    )
  }
  cancelCampaignPopup(c: CampaignRowItem) {
    this.setState({
      openModal: {
        campaign: c,
        modalType: 'cancel',
      },
    })
  }
  async cancelCampaign(c: CampaignRowItem, refetch: () => Promise<any>) {
    const newStatus = 'cancelled'
    this.setState({
      statusChanges: [
        ...this.state.statusChanges.filter((s) => s.id !== c.id),
        {
          id: c.id,
          fromStatus: c.status,
          toStatus: newStatus,
          loading: true,
        },
      ],
    })
    await setCampaignStatus(c.collectionId, c.id, newStatus)
    await refetch()
    this.setState({
      statusChanges: this.state.statusChanges.filter((s) => s.id !== c.id),
    })
  }
  deleteCampaign(campaign: CampaignRowItem) {
    this.setState({
      openModal: { campaign, modalType: 'delete' },
    })
  }
}

function eventTypeToDisplay(eventType: string, events?: number): string {
  const eventTypeWithoutUnderscore = eventType.replace(/_/, ' ')
  if (!events || events !== 1) {
    return eventTypeWithoutUnderscore + 's'
  }
  return eventTypeWithoutUnderscore
}

const withGql = graphql<InputProps, CampaignSummaryResponse>(
  getCampaignsQuery_v2,
  {
    options: ({ collectionId }) => ({
      variables: {
        collectionId: collectionId === 'all' ? '' : collectionId,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    }),
  }
)
const CampaignList = withGql(CampaignListView)

export { CampaignList }
